import {useMemo, useCallback} from 'react';
import {AccountConstants} from "finhelper";

interface AccountItem {
    id: number;
    name: string;
}

interface AccountFilterItem {
    value: number;
    label: string;
}

interface Account {
    accounts?: Array<{
        id: number;
        [key: string]: any;
    }>;
    [key: string]: any;
}

interface UseAccountFilterSyncReturn {
    filterAccountItems: AccountFilterItem[];
    setFilterAccountItems: (newItems: Array<AccountFilterItem | {id?: number; value?: number} | number>) => void;
}

/**
 * Хук для синхронизации фильтра счетов с актуальными данными
 * Хранит в localStorage только ID счетов, а метки (label) берет из актуальных данных accounts
 * 
 * @param filterAccountIds - Массив ID счетов из localStorage
 * @param accounts - Актуальный список счетов из Redux/API
 * @param setFilterAccountIds - Функция для обновления ID счетов в localStorage
 * @returns Объект с filterAccountItems и setFilterAccountItems
 */
const useAccountFilterSync = (
    filterAccountIds: number[] | undefined | null,
    accounts: Account[] | undefined | null,
    setFilterAccountIds: ((ids: number[]) => void) | undefined
): UseAccountFilterSyncReturn => {
    const filterAccountItems = useMemo(() => {
        if (!filterAccountIds || !Array.isArray(filterAccountIds) || !filterAccountIds.length || 
            !accounts || !Array.isArray(accounts) || !accounts.length) {
            return [];
        }

        // Получаем все доступные счета
        const allAccounts: AccountItem[] = accounts.flatMap(item => {
            if (!item.accounts) return [];
            return item.accounts.map(subAccount => ({
                id: subAccount.id,
                name: AccountConstants.getText(subAccount)
            }));
        });

        // Формируем синхронизированные объекты для фильтра
        return filterAccountIds
            .map(accountId => {
                const account = allAccounts.find(acc => acc && acc.id === accountId);
                if (account) {
                    return {
                        value: account.id,
                        label: account.name
                    };
                }
                // Если счет не найден (удален), возвращаем null для фильтрации
                return null;
            })
            .filter((item): item is AccountFilterItem => item !== null); // Убираем несуществующие счета
    }, [filterAccountIds, accounts]);

    // Функция для обновления фильтра (используется в AccountFilter)
    // Принимает массив объектов {value, label}, но сохраняет только ID
    const setFilterAccountItems = useCallback((
        newItems: Array<AccountFilterItem | {id?: number; value?: number} | number>
    ) => {
        if (!setFilterAccountIds) return;
        // Сохраняем только ID
        const newIds = newItems.map(item => {
            if (typeof item === 'number') return item;
            if (typeof item === 'object' && item !== null) {
                return (item as AccountFilterItem).value || (item as {id?: number}).id || item;
            }
            return item;
        }).filter((id): id is number => typeof id === 'number');
        setFilterAccountIds(newIds);
    }, [setFilterAccountIds]);

    return {
        filterAccountItems, // Синхронизированные объекты {value, label}
        setFilterAccountItems, // Функция для обновления (принимает объекты, сохраняет только ID)
    };
};

export default useAccountFilterSync;
